/**
 * Editor text-layer payload → PSD `LayerTextData`. Inverse of
 * `psd-text-import.ts::parsePsdText`. Tier 2 Group F PSD natives
 * follow-up (Phase C).
 *
 * The export path takes the SVG text provider's payload shape
 * (text content + position + TextSettings) and produces an ag-psd
 * `LayerTextData` record. Photoshop / Affinity / Krita / etc.
 * will parse the result as a native editable text layer rather
 * than a flat raster.
 *
 * ## Symmetry with import
 *
 * Position: import subtracts `fontSize * 0.8` from the SVG
 * baseline-y to get the editor's top-left. Export adds it back to
 * produce PSD's baseline. The same `BASELINE_TO_TOP_RATIO` is
 * mirrored on both sides so a PSD → editor → PSD round-trip stays
 * positionally stable.
 *
 * Text content: editor's `\n` (Fabric IText convention) → PSD's
 * `\r` paragraph break.
 *
 * ## Pragmatic simplifications
 *
 * - Single styleRun for the whole text — the editor's TextSettings
 *   is uniform per layer. Per-character runs from the original
 *   PSD aren't reconstructed.
 * - paragraphStyleRuns has a single entry with the editor's
 *   textAlign mapped back to PSD's `justification`.
 * - shapeType defaults to `'point'` (point text). Box text would
 *   need bounds metadata the editor doesn't carry.
 * - Anti-aliasing defaults to `'sharp'` to match Photoshop's
 *   typical default.
 */

import type { LayerTextData, TextStyle, ParagraphStyle } from 'ag-psd';
import type { TextSettings } from '../types';
import { BASELINE_TO_TOP_RATIO } from './text-baseline-constants';
import { hexToPsdColor } from './psd-color-utils';

/** Payload provided by the caller (EditorPage's handleExportPsd
 *  via `canvas.getLayerTextPayload`). Identical to
 *  `SvgExportTextPayload` so the same canvas helper feeds both
 *  formats. */
export interface PsdExportTextPayload {
  text: string;
  /** Scene-space TOP-LEFT of the text bbox. Inverse baseline
   *  adjustment is applied here. */
  x: number;
  y: number;
  settings: TextSettings;
}

// BASELINE_TO_TOP_RATIO imported from text-baseline-constants.ts.

/** Build the ag-psd `LayerTextData` from the editor's text
 *  payload. Returns a minimal but valid PSD text record that
 *  Photoshop / Affinity / Krita will render as native editable
 *  text. */
export function buildPsdTextRecord(payload: PsdExportTextPayload): LayerTextData {
  const { text, x, y, settings } = payload;
  // Editor's `\n` → PSD's `\r`. Round-trip safe: import normalizes
  // `\r` → `\n`, this normalizes back.
  const psdText = text.replace(/\n/g, '\r');

  // Position: editor top-left → PSD baseline. Inverse of the
  // import's `topY = baselineY - fontSize * 0.8`.
  const baselineY = y + settings.fontSize * BASELINE_TO_TOP_RATIO;

  const style: TextStyle = {
    font: { name: settings.fontFamily },
    fontSize: settings.fontSize,
    fauxBold: settings.fontWeight === 'bold',
    fauxItalic: settings.fontStyle === 'italic',
    underline: settings.underline,
    autoLeading: true,
    fillFlag: settings.fill !== 'transparent',
    fillColor: hexToPsdColor(settings.fill),
    ...(settings.letterSpacing && settings.letterSpacing !== 0
      ? { tracking: settings.letterSpacing }
      : {}),
    // lineHeight as `leading` in PSD's units — but only when the
    // editor's lineHeight isn't the default 1.16. When it is,
    // leave autoLeading on to match PSD's typical convention.
    ...(settings.lineHeight && Math.abs(settings.lineHeight - 1.2) > 0.01
      ? { autoLeading: false, leading: settings.fontSize * settings.lineHeight }
      : {}),
    // Stroke. PSD distinguishes outlineWidth + strokeColor for
    // text strokes (separate from vector strokes).
    ...(settings.stroke && settings.stroke.width > 0
      ? {
          strokeFlag: true,
          outlineWidth: settings.stroke.width,
          strokeColor: hexToPsdColor(settings.stroke.color),
        }
      : {}),
  };

  const paragraph: ParagraphStyle = {
    justification: mapTextAlignToJustification(settings.textAlign),
  };

  return {
    text: psdText,
    transform: [1, 0, 0, 1, x, baselineY],
    shapeType: 'point',
    styleRuns: [{ length: psdText.length, style }],
    paragraphStyleRuns: [{ length: psdText.length, style: paragraph }],
    antiAlias: 'sharp',
  };
}

/** Map editor `textAlign` ('left' | 'center' | 'right') → PSD
 *  paragraph `justification` ('left' | 'center' | 'right'). The
 *  editor doesn't model justified text; only the three non-
 *  justified variants are produced. */
function mapTextAlignToJustification(
  align: TextSettings['textAlign'],
): ParagraphStyle['justification'] {
  switch (align) {
    case 'right':
      return 'right';
    case 'center':
      return 'center';
    case 'left':
    default:
      return 'left';
  }
}

// hexToPsdColor imported from psd-color-utils.ts.
